import { signIn, SignInResponse } from "next-auth/react";
import { usePathname } from "next/navigation";
import { useState } from "react";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import * as Analytics from "../analytics";
import { isSignInResponseOk } from "../util/isAuthResponseOk";
import { AUTH_REDIRECT_SESSION_STORAGE_KEY } from "../components/AuthRedirectManager";
import { Routes } from "~/routes";

// // // //

interface Props {
    onAuthSuccess?: () => void;
}

/**
 * useInlineAuth
 * Encapsulated component logic for handling user authentication via Google or email/OTP verification
 */
export function useInlineAuth(props: Props) {
    const { onAuthSuccess } = props;

    // Use the current page as the post-authentication redirect path
    const pathname = usePathname();

    const { addToaster } = useToaster();

    const [currentStep, setCurrentStep] = useState<"auth" | "otp">("auth");
    const [isLoading, setIsLoading] = useState(false);
    const [userEmail, setUserEmail] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    async function handleEmailFormSubmit(email: string) {
        Analytics.trackInlineEmailLogin({ email });
        setIsLoading(true);
        setErrorMessage(null);
        setUserEmail(email);

        const response: SignInResponse | undefined = await signIn("email", {
            email,
            redirect: false,
        });

        if (!response) {
            Analytics.trackInlineEmailLoginResponseUndefined({ email });
            setErrorMessage(
                "Error sending verification email - please try again.",
            );
            setIsLoading(false);
            return;
        }

        const isResponseOk = isSignInResponseOk(response);

        if (!isResponseOk) {
            Analytics.trackInlineEmailLoginResponseOkFalse({ email });
            setErrorMessage(
                "Error sending verification email - please try again.",
            );
            addToaster({
                variant: "danger",
                message: "Error sending verification email - please try again.",
            });
            setIsLoading(false);
            return;
        }

        // Store the desired redirect path in sessionStorage (default to homepage if pathname is null)
        sessionStorage.setItem(
            AUTH_REDIRECT_SESSION_STORAGE_KEY,
            pathname ?? Routes.home,
        );
        Analytics.trackInlineEmailLoginSuccess({ email });
        setCurrentStep("otp");
        setIsLoading(false);
    }

    function handleGoogleSignIn() {
        Analytics.trackInlineGoogleRegister();

        // Store the desired redirect path in sessionStorage (default to homepage if pathname is null)
        sessionStorage.setItem(
            AUTH_REDIRECT_SESSION_STORAGE_KEY,
            pathname ?? Routes.home,
        );

        // Use default Google sign-in without callbackUrl
        signIn("google", {
            redirect: false,
        });
    }

    async function handleOTPSubmit(code: string, email: string) {
        setIsLoading(true);
        setErrorMessage(null);

        try {
            // Format email, code and callback parameters for the request URL
            const formattedEmail = encodeURIComponent(
                email.toLowerCase().trim(),
            );
            const formattedCode = encodeURIComponent(code);
            // const formattedCallback = encodeURIComponent(redirectPath);

            // Construct the NextAuth email callback URL with the formatted parameters
            // const otpRequestURL = `/api/auth/callback/email?email=${formattedEmail}&token=${formattedCode}&callbackUrl=${formattedCallback}&redirect=true`;
            const otpRequestURL = `/api/auth/callback/email?email=${formattedEmail}&token=${formattedCode}&redirect=true`;

            // Make request to verify the OTP code
            const response = await fetch(otpRequestURL);

            // If response is undefined, show error
            if (!response) {
                Analytics.trackInlineOTPResponseUndefined({ email });
                setErrorMessage("Verification failed - please try again.");
                return;
            }

            // If response is not ok, show error
            if (!response.ok) {
                Analytics.trackInlineOTPResponseOkFalse({ email, response });
                setErrorMessage(
                    "Invalid verification code - please try again.",
                );
                return;
            }

            // If response.status is not 200, show error
            if (response.status !== 200) {
                Analytics.trackInlineOTPResponseStatusNot200({
                    email,
                    response,
                });
                setErrorMessage("Verification failed - please try again.");
                return;
            }

            // If response.url includes /auth/error, show error
            if (response.url.includes("/auth/error")) {
                Analytics.trackInlineOTPResponseUrlIncludesAuthError({
                    email,
                    response,
                });
                setErrorMessage(
                    "Invalid verification code - please check your code.",
                );
                return;
            }

            // OTP verification successful
            Analytics.trackOTPSuccess({ email });

            // Call success callback if provided, otherwise refresh page
            if (onAuthSuccess) {
                onAuthSuccess();
            } else {
                window.location.reload();
            }
        } catch (error) {
            setErrorMessage("Verification failed - please try again.");
        } finally {
            setIsLoading(false);
        }
    }

    return {
        currentStep,
        isLoading,
        userEmail,
        errorMessage,
        handleEmailFormSubmit,
        handleGoogleSignIn,
        handleOTPSubmit,
    };
}
